import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import InstructorSidebar from "../common/instructorSidebar";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import Table from "../../../core/common/dataTable/index";
import { useDispatch } from "react-redux";
import { getAllQuizSubmissionApi } from "utils/adminThunk";
import Breadcrumb from "core/common/Breadcrumb/breadcrumb";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const safe = (v, fallback = "—") => (v === null || v === undefined || v === "" ? fallback : v);
const fmtDate = (d) => {
  try {
    return d ? new Date(d).toLocaleString() : "—";
  } catch {
    return "—";
  }
};

const fmtDate1 = (d) => {
  try {
    if (!d) return "—";
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return "—";
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  } catch {
    return "—";
  }
};


const joinAddress = (u = {}, s = {}) => {
  const parts = [
    s.present_street || u.present_street || u.street || u.address_line1,
    s.present_house_no || u.present_house_no,
    s.present_village || u.present_village,
    s.present_city || u.present_city || u.city,
    s.present_district || u.present_district,
    s.present_state || u.present_state || u.state,
    s.present_pincode || u.present_pincode || u.pincode || u.zip,
    u.country,
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : safe(u.address || s.address);
};

const InstructorQuizResult = () => {
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [dataSource, setDataSource] = useState([]);
  const [search, setSearch] = useState("");
  const [showReview, setShowReview] = useState(false);

  const dispatch = useDispatch();
  const router = useParams();
  const printRef = useRef(null);

  useEffect(() => {
    async function getAllResults(quizId) {
      const result = await dispatch(getAllQuizSubmissionApi({ quizId, search }));
      if (result?.payload?.success) {
        setDataSource(result?.payload?.submissions || []);
      }
    }
    if (router?.id) getAllResults(router?.id);
  }, [router, search, dispatch]);

  const openStudentModal = (record) => {
    setSelectedStudent(record);
    setShowReview(false); // reset on open
    const modal = document.getElementById("studentDetailModal");
    if (modal && window.bootstrap?.Modal) {
      const modalInstance =
        window.bootstrap.Modal.getInstance(modal) ||
        new window.bootstrap.Modal(modal);
      modalInstance.show();
    }
  };

  // Normalize answers for rendering
  const normalized = useMemo(() => {
    const submitted = selectedStudent?.submittedAnswers;
    if (!Array.isArray(submitted)) return [];
    return submitted.map((item, idx) => {
      const q = item?.questionId || {};
      const answers = Array.isArray(q.answers) ? q.answers : [];
      const correctTexts = answers.filter((a) => a?.istrue).map((a) => a?.text);
      const selected = Array.isArray(item?.selectedAnswers) ? item.selectedAnswers : [];
      const isSkipped = !!item?.skip || selected.length === 0;
      const isCorrect =
        !isSkipped &&
        selected.length === correctTexts.length &&
        selected.every((s) => correctTexts.includes(s));

      return {
        index: idx + 1,
        id: q?._id || String(idx),
        level: q?.level,
        type: q?.type,
        language: q?.language,
        quizQuestion: q?.quizQuestion,
        answers,
        correctTexts,
        selected,
        isSkipped,
        isCorrect,
      };
    });
  }, [selectedStudent]);

  // Stats + meta for header (works even if normalized is empty)
  const stats = useMemo(() => {
    const fromNormalized = normalized.length > 0;
    const total = fromNormalized
      ? normalized.length
      : selectedStudent?.totalQuestions || 0;
    const correct = fromNormalized
      ? normalized.filter((q) => q.isCorrect).length
      : selectedStudent?.correctAnswers || 0;
    const skipped = fromNormalized
      ? normalized.filter((q) => q.isSkipped).length
      : selectedStudent?.skippedQuestions || 0;
    const attempted = Math.max(0, total - skipped);
    const wrong = Math.max(0, attempted - correct);
    const percentage = total ? Math.round((correct / total) * 100) : 0;
    return { total, correct, wrong, skipped, attempted, percentage };
  }, [normalized, selectedStudent]);

  const meta = useMemo(() => {
    const u = selectedStudent?.user || {};
    const quiz = selectedStudent?.quiz || {};
    const status = selectedStudent?.status || {};
    const appliedFor =
      u?.csa_applications?.[0]?.apply_for ||
      selectedStudent?.course?.title ||
      selectedStudent?.program ||
      selectedStudent?.jobTitle ||
      "—";
    const registrationNo =
      selectedStudent?.registrationNumber ||
      selectedStudent?.registration_no ||
      u.registration_number ||
      u.registration_no ||
      "—";
    const applicationNo =
      u?.csa_applications?.[0]?.application_number ||
      "—";
    const dob = u?.date_of_birth || selectedStudent?.date_of_birth || null;
    const phone = u?.phone || u?.mobile || selectedStudent?.phone || "—";
    const email = u?.email || selectedStudent?.email || "—";
    const address = joinAddress(u, selectedStudent || {});
    const attemptDate =
      selectedStudent?.submittedAt || selectedStudent?.createdAt || null;
    const duration = selectedStudent?.duration || "—";
    const quizTitle = quiz?.quizTitle || quiz?.name || "Quiz";
    const passMarks = quiz?.passMarks|| "_";
    return {
      name: [u.first_name, u.last_name].filter(Boolean).join(" ") || "—",
      quizTitle,
      quizId: quiz?._id || quiz?.id || "—",
      submissionId: selectedStudent?._id || "—",
      appliedFor,
      registrationNo,
      applicationNo,
      dob: fmtDate1(dob),
      email,
      phone,
      address,
      attemptDate: fmtDate(attemptDate),
      passMarks,
      status
    };
  }, [selectedStudent]);

  // Build PDF from the visible panel (includes meta header + answers)
  const handleDownloadPDF = async () => {
    // Ensure there is at least something to print (meta or questions)
    const wasOpen = showReview;
    if (!wasOpen) setShowReview(true);
    await new Promise((r) => setTimeout(r, 50)); // let DOM paint

    const input = printRef.current;
    if (!input) return;

    const A4_WIDTH = 595.28; // pt
    const A4_HEIGHT = 841.89; // pt
    const SCALE = 2;

    const canvas = await html2canvas(input, {
      scale: SCALE,
      useCORS: true,
      backgroundColor: "#ffffff",
      windowWidth: input.scrollWidth,
      windowHeight: input.scrollHeight,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "pt", "a4");

    const imgWidth = A4_WIDTH;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= A4_HEIGHT;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= A4_HEIGHT;
    }

    const fileName = `${
      meta.quizTitle?.replace(/[^a-z0-9]+/gi, "-").toLowerCase() || "quiz"
    }-${(meta.name || "")
      .trim()
      .replace(/[^a-z0-9]+/gi, "-")
      .toLowerCase()}-${
      (meta.appliedFor || "").toLowerCase().replace(/[^a-z0-9]+/gi, "-") || "applied"
    }-${new Date().toISOString().slice(0, 10)}.pdf`;

    pdf.save(fileName);

    if (!wasOpen) setShowReview(false);
  };

  const columns = [
    {
      title: "Student Name",
      dataIndex: "StudentName",
      render: (text, record) => (
        <div className="d-flex align-items-center">
          <Link
            to="#"
            className="avatar avatar-md avatar-rounded flex-shrink-0 me-2"
            onClick={() => openStudentModal(record)}
          >
            <ImageWithBasePath
              src={
                record?.user?.photo_path
                  ? `https://indianairways.net/storage/${record?.user?.photo_path}`
                  : "assets/img/user/user-01.jpg"
              }
              alt=""
            />
          </Link>
          <Link to="#" onClick={() => openStudentModal(record)}>
            <p className="fs-14 mb-0">
              {record?.user?.first_name + " " + record?.user?.last_name}
            </p>
          </Link>
        </div>
      ),
    },
    {
      title: "Score",
      dataIndex: "totalMarksAchieved",
      render: (text, record) => {
        const correct = record?.correctAnswers || 0;
        const total = record?.totalQuestions || 0;
        return (
          <div className="d-flex align-items-center">
            <p className="fs-14 mb-0">{`${correct} / ${total}`}</p>
          </div>
        );
      },
    },
    {
      title: "Total Attempted Questions",
      dataIndex: "attemptedQuestions",
      render: (text, record) => (
        <div className="d-flex align-items-center text-center">
          <p className="fs-14 mb-0 ">
            {(record?.correctAnswers || 0) +
              ((record?.totalQuestions || 0) -
                ((record?.correctAnswers || 0) +
                  (record?.skippedQuestions || 0)))}
          </p>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text) => (
        <span
          className={`badge rounded-pill ${
            text === "pass" ? "bg-success" : "bg-danger"
          }`}
        >
          {text === "pass" ? "Pass" : "Fail"}
        </span>
      ),
    },
  ];

  return (
    <>
      <Breadcrumb title="Quiz Results" />
      <div className="content">
        <div className="container">
          <div className="row">
            <InstructorSidebar />
            <div className="col-lg-9">
              <div className="row my-3 ">
                <h5 className="page-title">Quiz Results</h5>
                <div className="col-md-12 d-flex justify-content-end">
                  <div className="position-relative col-12 col-md-4">
                    <input
                      type="text"
                      className="form-control ps-5"
                      placeholder="Search student's result here..."
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                      }}
                    />
                    <span
                      className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"
                      style={{ pointerEvents: "none" }}
                    >
                      <i className="bi bi-search"></i>
                    </span>
                  </div>
                </div>
              </div>

              <Table dataSource={dataSource} columns={columns} Search={false} />
            </div>
          </div>
        </div>
      </div>

      {/* Student Detail Modal */}
      <div className="modal fade" id="studentDetailModal" tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Student Details</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={() => setShowReview(false)}
              ></button>
            </div>

            <div className="modal-body">
              {selectedStudent ? (
                <>
                  <div className="d-flex flex-wrap gap-3 align-items-center justify-content-center mb-3">
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className={`btn ${
                          showReview ? "btn-outline-secondary" : "btn-primary"
                        }`}
                        onClick={() => setShowReview((v) => !v)}
                        title=""
                      >
                        {showReview ? "Hide Review" : "View Answers"}
                      </button>

                      <button
                        type="button"
                        className="btn btn-success cursor-pointer"
                        onClick={handleDownloadPDF}
                        title="Download as PDF"
                      >
                        Download PDF
                      </button>
                    </div>
                  </div>

                  {/* Printable Review + Meta (included in PDF) */}
                  {showReview && (
                    <div ref={printRef} className="border rounded p-3">
                      {/* Header / Candidate & Exam Details */}
                      <div className="pb-3 border-bottom mb-3">
                        <h5 className="mb-1">Candidate & Exam Details</h5>
                        <div className="row g-2 small">
                          <div className="col-md-6"><strong>Candidate Name : </strong> {safe(meta.name)}</div>
                          <div className="col-md-6"><strong>Applied For : </strong> {safe(meta.appliedFor)}</div>

                          <div className="col-md-6"><strong>Registration No : </strong> {safe(meta.registrationNo)}</div>
                          <div className="col-md-6"><strong>Application No : </strong> {safe(meta.applicationNo)}</div>

                          <div className="col-md-6"><strong>DOB : </strong> {safe(meta.dob)}</div>
                          <div className="col-md-6"><strong>Email : </strong> {safe(meta.email)}</div>

                          <div className="col-md-12"><strong>Address : </strong> {safe(meta.address)}</div>

                          <div className="col-md-6 mt-2"><strong>Quiz : </strong> {safe(meta.quizTitle)}</div>
                          {/* <div className="col-md-6 mt-2"><strong>Quiz ID:</strong> {safe(meta.quizId)}</div> */}

                          {/* <div className="col-md-6"><strong>Submission ID:</strong> {safe(meta.submissionId)}</div> */}
                          <div className="col-md-6"><strong>Attempt Date : </strong> {safe(meta.attemptDate)}</div>

                          <div className="col-md-6"><strong>PassMarks : </strong> {safe(meta.passMarks)}</div>
                          <div className="col-md-6"><strong>Status : </strong> {safe(meta.status)}</div>
                          <div className="col-md-6">
                            <strong>Summary : </strong>{" "}
                            {stats.correct} Correct, {stats.wrong} Wrong, {stats.skipped} Skipped ({stats.attempted} Attempted of {stats.total})
                          </div>
                        </div>
                      </div>

                      {/* Questions Review */}
                      <div className="pb-2">
                        <h5 className="mb-1">
                            Exam  Review
                        </h5>
                        <small className="text-muted">
                          Total Questions : {stats.total}
                        </small>
                      </div>

                      <ol className="ps-3 mt-3">
                        {normalized.length ? (
                          normalized.map((q) => (
                            <li key={q.id} className="mb-3">
                              <div className="d-flex justify-content-between align-items-start">
                                <h6 className="mb-2">
                                  <span className="text-muted me-2">
                                    Q{q.index}.
                                  </span>
                                  {q.quizQuestion}
                                </h6>
                                <div className="d-flex flex-wrap gap-2">
                                  {q.isSkipped ? (
                                    <span className="badge bg-warning-subtle text-dark border border-warning">
                                      Skipped
                                    </span>
                                  ) : q.isCorrect ? (
                                    <span className="badge bg-success">Correct</span>
                                  ) : (
                                    <span className="badge bg-danger">Incorrect</span>
                                  )}
                                  {q.level && (
                                    <span className="badge text-bg-light text-capitalize">
                                      {q.level}
                                    </span>
                                  )}
                                  {q.type && (
                                    <span className="badge text-bg-light">
                                      {q.type}
                                    </span>
                                  )}
                                </div>
                              </div>

                              <ul className="list-unstyled mb-2">
                                {q.answers.map((opt, i) => {
                                  const isSelected = q.selected.includes(opt.text);
                                  const isCorrect = q.correctTexts.includes(opt.text);

                                  let itemClass =
                                    "border rounded px-3 py-2 mb-2 d-flex align-items-center";
                                  if (isCorrect) itemClass += " border-success bg-success-subtle";
                                  else if (isSelected && !isCorrect)
                                    itemClass += " border-danger bg-danger-subtle";
                                  else itemClass += " border-secondary-subtle bg-light";

                                  return (
                                    <li className={itemClass} key={opt._id || i}>
                                      <span className="fw-medium me-2">
                                        {String.fromCharCode(65 + i)}.
                                      </span>
                                      <span className="flex-grow-1">{opt.text}</span>

                                      {isCorrect && (
                                        <span className="badge bg-success ms-2">Correct</span>
                                      )}
                                      {isSelected && !isCorrect && (
                                        <span className="badge bg-danger ms-2">Your choice</span>
                                      )}
                                      {isSelected && isCorrect && (
                                        <span className="badge bg-success ms-2">Your choice</span>
                                      )}
                                    </li>
                                  );
                                })}
                              </ul>
                            </li>
                          ))
                        ) : (
                          <li className="text-muted">No detailed answers available.</li>
                        )}
                      </ol>
                    </div>
                  )}

                  {!showReview && (
                    <div className="alert alert-info mt-3 mb-0">
                      Click <strong>View Answers</strong> to preview the detailed report
                      (this is exactly what will be exported in the PDF).
                    </div>
                  )}
                </>
              ) : (
                <p>No student selected</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InstructorQuizResult;
